import { useCallback, useEffect, useRef, useState } from 'react';

type ListboxNavigationOptions = {
  count: number;
  /** Off while a dialog owns the keyboard. */
  enabled: boolean;
  onOpen: (index: number) => void;
};

/**
 * Arrow keys move the highlight (and focus) through the options; Enter opens
 * the highlighted one. Works straight after page load, before anything is
 * focused, so the list is usable without touching the mouse.
 */
export function useListboxNavigation({ count, enabled, onOpen }: ListboxNavigationOptions) {
  const [activeIndex, setActiveIndex] = useState<number | null>(0);
  /** Last highlighted option; keeps the preview steady while the pointer is elsewhere. */
  const [previewIndex, setPreviewIndex] = useState(0);
  const optionRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const highlight = useCallback((index: number | null) => {
    setActiveIndex(index);
    if (index !== null) setPreviewIndex(index);
  }, []);

  useEffect(() => {
    if (!enabled || count === 0) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const fromBody = event.target === document.body;
      const fromOption = optionRefs.current.some((option) => option === event.target);
      if (!fromBody && !fromOption) return;

      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const next =
          event.key === 'ArrowDown'
            ? Math.min(count - 1, (activeIndex ?? -1) + 1)
            : Math.max(0, (activeIndex ?? 1) - 1);
        highlight(next);
        optionRefs.current[next]?.focus();
        return;
      }

      // A focused option handles Enter natively as a click.
      if (event.key === 'Enter' && fromBody && activeIndex !== null) {
        event.preventDefault();
        onOpen(activeIndex);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [activeIndex, count, enabled, highlight, onOpen]);

  const registerOption = useCallback(
    (index: number) => (element: HTMLButtonElement | null) => {
      optionRefs.current[index] = element;
    },
    [],
  );

  return { activeIndex, previewIndex, highlight, registerOption };
}
